/**
 * V2 Session Helper — clean access to the PalCare app session.
 *
 * The PalCare session (dData) is stored as plain JSON in sessionStorage
 * under the key "appSession". Auth tokens are managed separately by
 * AWS Amplify — this module only handles app-level session data
 * (campus, role, permissions, filters, etc.).
 */

// ---------------------------------------------------------------------------
// Core accessor
// ---------------------------------------------------------------------------

/**
 * Returns the full session object (dData), or null if not logged in.
 *
 * @returns {object | null}
 */
export function getSession() {
  try {
    const raw = sessionStorage.getItem('appSession');
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (err) {
    console.error('[session] Failed to read session:', err);
    return null;
  }
}

/**
 * Writes an updated session object back to storage.
 *
 * @param {object} dData — The full session object to persist
 */
export function setSession(dData) {
  try {
    sessionStorage.setItem('appSession', JSON.stringify(dData));
    // sessionStorage doesn't fire `storage` events in the same tab, so anything
    // that needs to react to mid-session changes (e.g. portal_afr toggling) has
    // to listen for this custom event instead.
    window.dispatchEvent(new CustomEvent('session:updated'));
  } catch (err) {
    console.error('[session] Failed to write session:', err);
  }
}

// ---------------------------------------------------------------------------
// Convenience accessors
// ---------------------------------------------------------------------------

/** Current campus ID */
export function getCampusId() {
  return getSession()?.acnt ?? null;
}

/** My campus ID */
export function getMyCampusId(){
  return getSession()?.myAcnt ?? null;
}

/** Current user ID */
export function getUserId() {
  return getSession()?.user ?? null;
}

/** Current session ID (used as ClientId in API calls) */
export function getSessionId() {
  return getSession()?.sessionId ?? null;
}

/** User's last-acknowledged portal version (X.Y.Z), or null if never set */
export function getPortalVersion() {
  return getSession()?.portal_version ?? null;
}

/** Campus-specific configuration for the current campus */
export function getCampusConfig() {
  const s = getSession();
  if (!s?.acnt || !s?.campusData) return null;
  return s.campusData[s.acnt.toString()] ?? null;
}

/** Campus name */
export function getCampusName() {
  return getSession()?.campus ?? '';
}

/**
 * Full campus list for the campus selector dropdown.
 *
 * dData.acntList is an array of plain campus IDs (e.g. [1, 5, 12]).
 * Campus names/config live in dData.campusData[id]. This function
 * transforms the raw IDs into objects the V2 UI expects.
 *
 * @returns {Array<{campus_id: number, campus_name: string, valid: boolean}>}
 */
export function getCampusList() {
  const s = getSession();
  if (!s?.acntList || !s?.campusData) return [];
  return s.acntList
    .map((id) => ({
      campus_id: id,
      campus_name: s.campusData[id.toString()]?.name || `Campus ${id}`,
      valid: s.campusData[id.toString()]?.valid ?? true,
    }))
    // Alphabetical by name so every V2 dropdown matches the classic portal's order.
    .sort((a, b) => a.campus_name.localeCompare(b.campus_name, undefined, { numeric: true, sensitivity: 'base' }));
}

/**
 * Persist a campus change to the session.
 * Mirrors V1's setACNT() — updates dData.acnt and copies
 * campus-specific feature flags to the root session object.
 *
 * @param {number} campusId — The new campus ID to persist
 */
export function updateSessionCampus(campusId) {
  const s = getSession();
  if (!s) return;

  const id = campusId.toString();
  const cfg = s.campusData?.[id] || {};

  s.acnt = campusId;
  s.campus = cfg.name || s.campus;
  s.rounds = cfg.is_rounding || 0;
  s.checkin = cfg.is_resident_checkin || 0;
  s.evmSiteId = cfg.location_site_id || '0';
  s.cloud = cfg.is_cloud || 0;
  s.hybrid = cfg.is_hybrid || false;
  s.legacy = cfg.show_legacy || false;
  s.new = cfg.show_new || false;
  s.server_version = cfg.server_version || '';

  setSession(s);
}

/**
 * Effective user role weight (for permission checks).
 *
 * When impersonating, `dData.role_weight` reflects the impersonated role
 * (e.g. 10 for Caregiver), while `dData.my_role_weight` remains the
 * original login role (e.g. 1000 for PalCare Admin). Authorization
 * checks must respect the impersonated role — otherwise
 * `iAmHigherAuthority`'s `>= 1000` short-circuit lets the impersonator
 * bypass every role-weight gate. Fall back to `my_role_weight` only if
 * the session was written before impersonation was recorded.
 */
export function getRoleWeight() {
  const s = getSession();
  return s?.role_weight ?? 10;
}

export function getMyRoleWeight(){
  const s = getSession();
  return s?.my_role_weight ?? 10;
}

/** Whether user has at least the given role weight */
export function hasMinRole(minWeight) {
  return getRoleWeight() >= minWeight;
}

/**
 * Converts a campus ID to its GUID for DynamoDB operations.
 * Wraps the V1 `campusToGuid(dData, campusId)` pattern from libs.jsx.
 *
 * @param {number|string} [campusId] — Campus ID to convert, defaults to current campus
 * @returns {string} Campus GUID, or the campus ID as fallback
 */
export function getCampusGuid(campusId) {
  const s = getSession();
  if (!s) return campusId ?? '';
  const id = (campusId ?? s.acnt)?.toString();
  return s.campusData?.[id]?.guid || id;
}

/**
 * Returns the current user's username (for ModifiedBy fields).
 */
export function getUsername() {
  return getSession()?.username ?? '';
}

/**
 * Returns an array of vendor_ids for every active vendor on a campus (current
 * campus by default). Used to populate the `vendor_ids` field on transaction
 * payloads (replaces the old offsite/offsite_id pair).
 */
export function getActiveVendorIdList(campusId){
  return getAllVendors(campusId)
    .filter((v) => v?.vendor_active)
    .map((v) => v?.vendor_id)
    .filter((id) => id != null);
}

// ---------------------------------------------------------------------------
// Third-party vendor selectors
//
// Each campus config carries a `vendors` array (see getMyCorpCampusDetails on
// the backend). Vendor shape:
//   { vendor_id, vendor_type_id, vendor_name, vendor_active,
//     is_receiving, is_receiving_user, stg_count }
// vendor_type_id: 1 = EHR, 2 = Software, 3 = Offsite Monitoring.
//
// All selectors default to the current campus (dData.acnt); pass a campusId to
// target a specific campus.
// ---------------------------------------------------------------------------

/** All vendors for a campus (or current campus when campusId is omitted). */
export function getAllVendors(campusId) {
  const s = getSession();
  if (!s?.campusData) return [];
  const cid = campusId ?? s.acnt;
  if (cid == null) return [];
  const cfg = s.campusData[cid.toString()];
  return Array.isArray(cfg?.vendors) ? cfg.vendors : [];
}

/** EHR vendors (vendor_type_id === 1). */
export function getEHRVendors(campusId) {
  return getAllVendors(campusId).filter((v) => v?.vendor_type_id === 1);
}

/** Software vendors (vendor_type_id === 2). */
export function getSoftwareVendors(campusId) {
  return getAllVendors(campusId).filter((v) => v?.vendor_type_id === 2);
}

/** Offsite monitoring vendors (vendor_type_id === 3). */
export function getOffsiteMonitoringVendors(campusId) {
  return getAllVendors(campusId).filter((v) => v?.vendor_type_id === 3);
}

/**
 * Primary EHR vendor info for a campus (current campus by default), mapped to
 * the flat shape the app consumes. Replaces the old per-campus ehr_* config
 * fields. Prefers an active EHR vendor, else the first configured one.
 *   { active, vendor_id, vendor_name, stg_count,
 *     receiving_resident (is_receiving), receiving_user (is_receiving_user) }
 */
export function getEhrInfo(campusId) {
  const vendors = getEHRVendors(campusId);
  const vendor = vendors.find((v) => v?.vendor_active) || vendors[0] || null;
  return {
    active: !!vendor?.vendor_active,
    vendor_id: vendor?.vendor_id ?? 0,
    vendor_name: vendor?.vendor_name ?? '',
    stg_count: vendor?.stg_count ?? 0,
    receiving_resident: !!vendor?.is_receiving,
    receiving_user: !!vendor?.is_receiving_user,
  };
}

/**
 * Update the staging-task count on the campus's primary EHR vendor and persist
 * the session. Used to live-refresh the EHR badge without a full reload
 * (replaces writing the old ehr_stg_count flat field).
 */
export function setEhrStgCount(campusId, count) {
  const s = getSession();
  if (!s?.campusData) return;
  const cid = campusId ?? s.acnt;
  if (cid == null) return;
  const vendors = s.campusData[cid.toString()]?.vendors;
  if (!Array.isArray(vendors)) return;
  const vendor = vendors.find((v) => v?.vendor_type_id === 1 && v?.vendor_active)
    || vendors.find((v) => v?.vendor_type_id === 1)
    || null;
  if (!vendor) return;
  vendor.stg_count = count;
  setSession(s);
}

/**
 * Set the active state of a specific vendor (by vendor_id) on a campus in the
 * session and persist. Used after toggling a third-party integration so the
 * derived selectors (getEhrInfo, getOffsiteMonitoringVendors, etc.) reflect the change
 * without a re-login. Returns true when the vendor was found and updated.
 */
export function setVendorActive(campusId, vendorId, active) {
  const s = getSession();
  if (!s?.campusData) return false;
  const cid = campusId ?? s.acnt;
  if (cid == null) return false;
  const vendors = s.campusData[cid.toString()]?.vendors;
  if (!Array.isArray(vendors)) return false;
  const vendor = vendors.find((v) => String(v?.vendor_id) === String(vendorId));
  if (!vendor) return false;
  vendor.vendor_active = !!active;
  setSession(s);
  return true;
}
